import type { ReplyMode, ReplyStatus } from './reply-modes';

const minutes = (count: number) => (count === 1 ? '1 minute' : `${count} minutes`);

/** The reply mode choices, in the order the Replies page and setup offer them. */
export const REPLY_MODE_OPTIONS: Array<{
  value: ReplyMode;
  label: string;
  description: (delayMinutes: number) => string;
}> = [
  {
    value: 'auto',
    label: 'Reply automatically',
    description: () => 'The AI answers customers right away.',
  },
  {
    value: 'delayed',
    label: 'Wait, then reply',
    description: (delayMinutes) =>
      `The AI gives your team ${minutes(delayMinutes)} to answer first.`,
  },
  {
    value: 'assist',
    label: 'Draft replies for review',
    description: () => 'The AI writes a draft. Your team reviews and sends it.',
  },
  {
    value: 'off',
    label: "Don't reply",
    description: () => 'The AI stays out of new conversations.',
  },
];

/** The two periods the reply modes apply to, from the business hours. */
export const REPLY_PERIOD_LABELS: Record<'open' | 'closed', string> = {
  open: "While you're open",
  closed: "While you're closed",
};

/**
 * Names for each AI reply mode, shared by the Replies setting and the Overview status card
 * so the two always point at each other with the same words.
 */
export const REPLY_MODE_SETTING_NAMES: Record<string, string> = {
  auto: 'Reply automatically',
  delayed: 'Wait, then reply',
  assist: 'Draft replies for review',
  off: "Don't reply",
};

export const REPLY_MODE_STATUS_NAMES: Record<string, string> = {
  auto: 'Full Service',
  delayed: 'On Standby',
  assist: 'Assist',
  off: 'Paused',
};

/** What each mode does, completing "You're open, so …" for the mode in effect now. */
export const MODE_META: Record<string, { dot: string; description: (delayMinutes: number) => string }> = {
  auto: {
    dot: 'bg-emerald-500',
    description: () => 'the AI replies to customers right away.',
  },
  delayed: {
    dot: 'bg-blue-500',
    description: (delayMinutes) =>
      `the AI waits ${delayMinutes === 1 ? '1 minute' : `${delayMinutes} minutes`} for your team to reply, then answers.`,
  },
  assist: {
    dot: 'bg-violet-500',
    description: () => 'the AI drafts replies for your team to review.',
  },
  off: {
    dot: 'bg-muted-foreground/50',
    description: () => "the AI isn't responding to any messages right now.",
  },
};

/** "You're open, so the AI …", from the mode in effect now and the period it belongs to. */
export function describeCurrentMode(
  mode: string,
  period: ReplyStatus['period'],
  delayMinutes: number,
  repliesInOtherPeriod: boolean,
) {
  // Off in both periods is the owner's choice, not a consequence of being open or closed.
  if (mode === 'off' && !repliesInOtherPeriod)
    return 'The AI is off in both open and closed hours.';
  const description =
    mode === 'off' && repliesInOtherPeriod && (period === 'open' || period === 'closed')
      ? `the AI stays out of new conversations until you ${period === 'open' ? 'close' : 'open'}.`
      : MODE_META[mode].description(delayMinutes);
  if (period === 'open') return `You're open, so ${description}`;
  if (period === 'closed') return `You're closed, so ${description}`;
  return description.charAt(0).toUpperCase() + description.slice(1);
}
